import { CountryCode, getCountries, getCountryCallingCode, parsePhoneNumber, PhoneNumber } from "libphonenumber-js/min";

interface MappingCache {
    [key: string]: string;
}

const callingCodeCountryCache: MappingCache = {};

export const formatPrefix = (prefix: string): string => {
    if (/^\+[0-9]{1,3}$/.test(prefix)) {
        return prefix;
    }
    if (prefix.length === 0) {
        return "";
    }
    return `+${prefix.replace(/[^0-9]/g, "")}`;
};

export const getCallingCodeForCountry = (country: string, fallback: string = ""): string => {
    let callingCode: string;
    try {
        callingCode = getCountryCallingCode(country as CountryCode).valueOf();
    } catch {
        callingCode = fallback;
    }
    return callingCode;
};

export const getCountryForCallingCode = (prefix: string): string => {
    if (typeof prefix !== "string" || prefix.length === 0) {
        return "";
    }
    const callingCode: string = prefix.replace(/[^0-9]/g, "").valueOf();
    if (callingCodeCountryCache[callingCode] !== undefined) {
        return callingCodeCountryCache[callingCode];
    }
    const countries = getCountries();
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < countries.length; i++) {
        if (getCountryCallingCode(countries[i]).valueOf() === callingCode) {
            callingCodeCountryCache[callingCode] = countries[i].valueOf();
            return callingCodeCountryCache[callingCode];
        }
    }
    return "";
};

const fixCountryCode = (country: string): string => {
    if (typeof country !== "string" || country.length !== 2) {
        return "";
    }
    const countryCode: string = country.toUpperCase().valueOf();
    return getCountries()
        .map((value) => value.valueOf())
        .includes(countryCode)
        ? countryCode
        : "";
};

export interface PhoneNumberValidator {
    getCountry: () => string;
    getPrefix: () => string;
    getFormattedValue: () => string;
    getValidatedValue: () => string;
    isValid: () => boolean;
}

export const getValidator = (value: string = "", defaultCountry?: string): PhoneNumberValidator => {
    const countryCode: CountryCode = defaultCountry as CountryCode;
    let phoneNumber: PhoneNumber | null = null;
    try {
        phoneNumber = parsePhoneNumber(value, countryCode);
    } catch {
        phoneNumber = null;
    }

    let prefix = "";
    let formattedValue: string = value.replace(/[^+0-9 ]/g, "");
    // @ts-expect-error Check for typing
    let country: string = fixCountryCode(defaultCountry);
    let isValid = false;

    if (phoneNumber?.country !== undefined) {
        prefix = formatPrefix(phoneNumber.countryCallingCode.valueOf());
        country = phoneNumber.country.valueOf();
        formattedValue = phoneNumber.formatInternational();
        isValid = phoneNumber.isValid();
    } else if (countryCode !== undefined) {
        prefix = formatPrefix(getCallingCodeForCountry(countryCode));
        if (formattedValue === "") {
            formattedValue = prefix;
        }
    }

    return {
        getPrefix: () => prefix,
        getCountry: () => country,
        isValid: () => isValid,
        getFormattedValue: () => formattedValue,
        getValidatedValue: () => (isValid ? formattedValue.replace(/\s/g, "") : "")
    };
};
